import { useCallback, useRef } from "react";
import {
  BLOCKED_IMAGE_TYPES_CT_MR,
  DICOM_PIXEL_SPACING,
  FILE_LIMITS,
  POSTURAL_EXTENSIONS,
  WARNED_IMAGE_TYPES_DX_CR,
} from "../classes/constants/fileConstants";
import { useNotification } from "../classes/contexts/NotificationContext";
import { FileCategories, MODALITY } from "../classes/enums/MedicalImagesEnum";
import { SnackbarType } from "../classes/enums/SnackbarTypeEnum";
import { getFileExtension } from "../classes/utils/FileUtils";

// Modalities that require the pixel spacing check
const XRAY_MODALITIES = new Set([MODALITY.DX, MODALITY.CR]);
// Modalities that require orientation + rescale checks
const VOLUME_MODALITIES = new Set([MODALITY.CT, MODALITY.MR]);

/**
 * Returns a stable key for a processed file object.
 * Mirrors the logic in useFileManagement.removeFile for consistency.
 */
const getFileKey = file => {
  if (file.instanceId) return `inst_${file.instanceId}`;
  if (file.file?.size !== undefined && file.file?.lastModified) {
    return `file_${file.name}_${file.file.size}_${file.file.lastModified}`;
  }
  return `name_${file.name}_${file.seriesId || ""}_${file.studyId || ""}`;
};

/**
 * Calculates age in years from a DICOM birth date string (YYYYMMDD).
 * Returns null if the date is invalid.
 */
const ageFromDicomDate = birthDateStr => {
  if (!birthDateStr || birthDateStr.length !== 8) return null;
  const year  = parseInt(birthDateStr.substring(0, 4), 10);
  const month = parseInt(birthDateStr.substring(4, 6), 10) - 1;
  const day   = parseInt(birthDateStr.substring(6, 8), 10);
  const dob   = new Date(year, month, day);
  if (isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const hasHadBirthday =
    now.getMonth() > dob.getMonth() ||
    (now.getMonth() === dob.getMonth() && now.getDate() >= dob.getDate());
  if (!hasHadBirthday) age--;
  return age;
};

/**
 * Hook that validates processed file objects and fires snackbar notifications
 * for any issues found.
 *
 * Usage:
 *   const { validateFiles, validateGroups } = useFileValidation();
 *
 *   // After processFilesInBatches:
 *   const rejectedKeys = validateFiles(processedFiles);
 *   const validFiles = processedFiles.filter(f => !rejectedKeys.has(getFileKey(f)));
 *
 *   // After groupFilesById:
 *   validateGroups(problematicGroups);
 */
export const useFileValidation = () => {
  const { addNotification } = useNotification();

  // Deduplication ref — prevents firing the same warning twice when
  // renderContent() re-runs on every render.
  const firedGroupWarnings = useRef(new Set());

  /**
   * Validates an array of processed file objects.
   * Returns a Set of file keys that should be rejected (not added to state).
   * Fires batched ERROR/WARNING notifications for issues found.
   *
   * @param {object[]} processedFiles - Output of processFilesInBatches()
   * @returns {Set<string>} rejectedKeys
   */
  const validateFiles = useCallback(
    (processedFiles, allowedFileTypes = []) => {
      const rejectedKeys = new Set();

      // ── Batch-level checks ────────────────────────────────────────────────

      if (processedFiles.length > FILE_LIMITS.MAX_FILES_PER_BATCH) {
        addNotification(
          SnackbarType.ERROR,
          `Import aborted: too many files (${processedFiles.length.toLocaleString()}). Maximum is ${FILE_LIMITS.MAX_FILES_PER_BATCH.toLocaleString()} files per import.`,
          { duration: 6000 }
        );
        // Reject everything
        processedFiles.forEach(f => rejectedKeys.add(getFileKey(f)));
        return rejectedKeys;
      }

      const totalBytes = processedFiles.reduce(
        (sum, f) => sum + (f.file?.size || 0),
        0
      );
      if (totalBytes > FILE_LIMITS.MAX_BATCH_SIZE_BYTES) {
        addNotification(
          SnackbarType.ERROR,
          `Import aborted: total size exceeds the 10 GB limit.`,
          { duration: 6000 }
        );
        processedFiles.forEach(f => rejectedKeys.add(getFileKey(f)));
        return rejectedKeys;
      }

      // ── Per-file checks ───────────────────────────────────────────────────

      // Counters for batched notifications
      const errors = {
        unsupportedFormat: [],
        oversizePostural: [],
        oversizeMedical: [],
        missingPixelData: [],
        unsupportedModality: [],
        missingUid: [],
        blockedImageType: [],
        niftiV2: [],
        niftiAxisTooLarge: [],
      };

      const warnings = {
        missingMetadata: [],
        missingOrientation: [],
        missingRescale: [],
        pixelSpacingAbsent: [],
        pixelSpacingOutOfRange: [],
        warnedImageType: [],
        underage: [],
      };

      for (const file of processedFiles) {
        const key  = getFileKey(file);
        const size = file.file?.size || 0;
        const meta = file.additionalMetadata || {};
        const mod  = file.modality;
        const ext  = getFileExtension(file.file || { name: file.name });

        // ── Unsupported format ──────────────────────────────────────────────
        if (file.error === "unsupported-format") {
          errors.unsupportedFormat.push(file.name || file.file?.name || "unknown");
          rejectedKeys.add(key);
          continue;
        }

        // ── File size ───────────────────────────────────────────────────────
        if (POSTURAL_EXTENSIONS.has(ext)) {
          if (size > FILE_LIMITS.MAX_POSTURAL_FILE_SIZE_BYTES) {
            errors.oversizePostural.push(file.name);
            rejectedKeys.add(key);
            continue;
          }
        } else {
          // DICOM / NIfTI / unknown
          if (size > FILE_LIMITS.MAX_MEDICAL_FILE_SIZE_BYTES) {
            errors.oversizeMedical.push(file.name);
            rejectedKeys.add(key);
            continue;
          }
        }

        // ── Missing metadata (DICOM / NIfTI only) ───────────────────────────
        const isMedicalFile =
          file.detectedType === FileCategories.DICOM ||
          file.detectedType === FileCategories.NIFTI ||
          ext === ".dcm" || ext === ".dicom" ||
          ext === ".nii" || ext === ".nii.gz";

        if (isMedicalFile && (!file.creationDate || !file.modality)) {
          warnings.missingMetadata.push(file.name);
        }

        // ── DICOM-specific ──────────────────────────────────────────────────
        if (file.detectedType === FileCategories.DICOM || ext === ".dcm" || ext === ".dicom") {

          // Pixel Data presence
          if (!meta.pixelDataPresent) {
            errors.missingPixelData.push(file.name);
            rejectedKeys.add(key);
            continue;
          }

          // Modality supported
          const supportedModalities = new Set(["CR", "CT", "DX", "MR"]);
          if (!mod || !supportedModalities.has(mod)) {
            errors.unsupportedModality.push(file.name);
            rejectedKeys.add(key);
            continue;
          }

          // Mandatory UIDs
          if (!file.seriesId || !file.studyId) {
            errors.missingUid.push(file.name);
            rejectedKeys.add(key);
            continue;
          }

          // ImageType — block LOCALIZER/SCOUT etc for CT/MR
          if (VOLUME_MODALITIES.has(mod) && meta.imageType?.length >= 3) {
            const field3 = meta.imageType[2];
            if (BLOCKED_IMAGE_TYPES_CT_MR.some(blocked => field3.includes(blocked))) {
              errors.blockedImageType.push(file.name);
              rejectedKeys.add(key);
              continue;
            }
          }

          // ImageType — warn DERIVED/SECONDARY for DX/CR
          if (XRAY_MODALITIES.has(mod) && meta.imageType?.length >= 2) {
            const field1 = meta.imageType[0];
            const field2 = meta.imageType[1];
            if (
              WARNED_IMAGE_TYPES_DX_CR.includes(field1) ||
              WARNED_IMAGE_TYPES_DX_CR.includes(field2)
            ) {
              if (!warnings.warnedImageType.includes(file.name)) {
                warnings.warnedImageType.push(file.name);
              }
            }
          }

          // ImageOrientationPatient — warn if missing for CT/MR
          if (VOLUME_MODALITIES.has(mod) && !file.orientation) {
            warnings.missingOrientation.push(file.name);
          }

          // Rescale Intercept/Slope — warn if missing for CT
          if (
            mod === MODALITY.CT &&
            (meta.rescaleIntercept === undefined || meta.rescaleSlope === undefined)
          ) {
            warnings.missingRescale.push(file.name);
          }

          // Pixel spacing — DX/CR
          if (XRAY_MODALITIES.has(mod)) {
            const spacing = meta.pixelSpacing || meta.imagerPixelSpacing;
            if (!spacing) {
              warnings.pixelSpacingAbsent.push(file.name);
            } else {
              const [row, col] = spacing;
              const outOfRange =
                row < DICOM_PIXEL_SPACING.MIN_MM ||
                row > DICOM_PIXEL_SPACING.MAX_MM ||
                col < DICOM_PIXEL_SPACING.MIN_MM ||
                col > DICOM_PIXEL_SPACING.MAX_MM;
              if (outOfRange) {
                warnings.pixelSpacingOutOfRange.push(file.name);
              }
            }
          }

          // Patient age < 18 — warn
          const birthDate = file.patientInfo?.birthDate;
          if (birthDate) {
            const age = ageFromDicomDate(birthDate.replace(/-/g, ""));
            if (age !== null && age < 18) {
              warnings.underage.push(file.name);
            }
          }
        }

        // ── NIfTI-specific ──────────────────────────────────────────────────
        if (file.detectedType === FileCategories.NIFTI || ext === ".nii" || ext === ".nii.gz") {

          // NIfTI-2 rejection
          if (meta.niftiVersion && meta.niftiVersion !== 1) {
            errors.niftiV2.push(file.name);
            rejectedKeys.add(key);
            continue;
          }

          // Axis size limit
          if (meta.maxDimension && meta.maxDimension > 32767) {
            errors.niftiAxisTooLarge.push(file.name);
            rejectedKeys.add(key);
            continue;
          }
        }
      }

      // ── Fire batched notifications ─────────────────────────────────────────

      // Errors
      if (errors.unsupportedFormat.length > 0) {
        const formatsLabel = allowedFileTypes.length > 0
          ? allowedFileTypes.join(", ")
          : "supported formats";
        addNotification(
          SnackbarType.ERROR,
          `${errors.unsupportedFormat.length} file(s) rejected: unsupported format. Allowed formats: ${formatsLabel}.`,
          { duration: 6000 }
        );
      }
      if (errors.oversizePostural.length > 0) {
        addNotification(
          SnackbarType.ERROR,
          `${errors.oversizePostural.length} postural image(s) rejected: file size exceeds 100 MB limit.`,
          { duration: 6000 }
        );
      }
      if (errors.oversizeMedical.length > 0) {
        addNotification(
          SnackbarType.ERROR,
          `${errors.oversizeMedical.length} medical image(s) rejected: file size exceeds 1 GB limit.`,
          { duration: 6000 }
        );
      }
      if (errors.missingPixelData.length > 0) {
        addNotification(
          SnackbarType.ERROR,
          `${errors.missingPixelData.length} DICOM file(s) rejected: no pixel data found.`,
          { duration: 6000 }
        );
      }
      if (errors.unsupportedModality.length > 0) {
        addNotification(
          SnackbarType.ERROR,
          `${errors.unsupportedModality.length} DICOM file(s) rejected: unsupported or missing modality. Supported: CR, CT, DX, MR.`,
          { duration: 6000 }
        );
      }
      if (errors.missingUid.length > 0) {
        addNotification(
          SnackbarType.ERROR,
          `${errors.missingUid.length} DICOM file(s) rejected: missing Series or Study Instance UID.`,
          { duration: 6000 }
        );
      }
      if (errors.blockedImageType.length > 0) {
        addNotification(
          SnackbarType.ERROR,
          `${errors.blockedImageType.length} DICOM CT/MR image(s) rejected: image type is LOCALIZER, SCOUT, POST-PROCESSED or SCREEN SAVE.`,
          { duration: 6000 }
        );
      }
      if (errors.niftiV2.length > 0) {
        addNotification(
          SnackbarType.ERROR,
          `${errors.niftiV2.length} NIfTI file(s) rejected: only NIfTI-1 format is supported.`,
          { duration: 6000 }
        );
      }
      if (errors.niftiAxisTooLarge.length > 0) {
        addNotification(
          SnackbarType.ERROR,
          `${errors.niftiAxisTooLarge.length} NIfTI file(s) rejected: an image axis exceeds the 32,767 pixel limit.`,
          { duration: 6000 }
        );
      }

      // Warnings
      if (warnings.missingMetadata.length > 0) {
        addNotification(
          SnackbarType.WARNING,
          `${warnings.missingMetadata.length} image(s) have missing or incomplete metadata. Some information may not be displayed correctly. You will need to fill in the required fields before uploading.`,
          { duration: 8000 }
        );
      }
      if (warnings.missingOrientation.length > 0) {
        addNotification(
          SnackbarType.WARNING,
          `${warnings.missingOrientation.length} CT/MR DICOM image(s) are missing Image Orientation data — 3D reconstruction may be inaccurate.`,
          { duration: 6000 }
        );
      }
      if (warnings.missingRescale.length > 0) {
        addNotification(
          SnackbarType.WARNING,
          `${warnings.missingRescale.length} CT DICOM image(s) are missing Rescale Intercept/Slope — Hounsfield unit values may be inaccurate.`,
          { duration: 6000 }
        );
      }
      if (warnings.pixelSpacingAbsent.length > 0) {
        addNotification(
          SnackbarType.WARNING,
          `${warnings.pixelSpacingAbsent.length} DX/CR image(s) are missing pixel spacing information — distance measurements will not be available.`,
          { duration: 6000 }
        );
      }
      if (warnings.pixelSpacingOutOfRange.length > 0) {
        addNotification(
          SnackbarType.WARNING,
          `${warnings.pixelSpacingOutOfRange.length} DX/CR image(s) have pixel spacing outside the valid range (${DICOM_PIXEL_SPACING.MIN_MM}–${DICOM_PIXEL_SPACING.MAX_MM} mm) — measurements may be inaccurate.`,
          { duration: 6000 }
        );
      }
      if (warnings.warnedImageType.length > 0) {
        addNotification(
          SnackbarType.WARNING,
          `${warnings.warnedImageType.length} DX/CR image(s) are DERIVED or SECONDARY — image quality may be reduced.`,
          { duration: 6000 }
        );
      }
      if (warnings.underage.length > 0) {
        addNotification(
          SnackbarType.WARNING,
          `${warnings.underage.length} image(s) appear to belong to a patient under 18 years old.`,
          { duration: 6000 }
        );
      }

      return rejectedKeys;
    },
    [addNotification]
  );

  /**
   * Validates orientation consistency across groups.
   * Should be called after groupFilesById() with its returned problematicGroups.
   * Fires one WARNING notification per affected group.
   * Deduplicates — safe to call on every render.
   *
   * @param {object[]} problematicGroups - The problematicGroups array from groupFilesById()
   */
  const validateGroups = useCallback(
    problematicGroups => {
      if (!problematicGroups || problematicGroups.length === 0) return;

      for (const group of problematicGroups) {
        // Create a stable key for this group + excluded count to avoid re-firing
        const notifKey = `${group.originalGroupId}:${group.filteredFiles.length}`;
        if (firedGroupWarnings.current.has(notifKey)) continue;

        firedGroupWarnings.current.add(notifKey);

        const count = group.filteredFiles.length;
        const seriesLabel = group.originalGroupId || "unknown series";

        addNotification(
          SnackbarType.WARNING,
          `Series "${seriesLabel}": ${count} slice(s) excluded — inconsistent image orientation. Only slices matching the dominant orientation are kept.`,
          { duration: 8000 }
        );
      }
    },
    [addNotification]
  );

  /**
   * Resets the deduplication set for group warnings.
   * Call this when the uploaded files list is cleared.
   */
  const resetGroupWarnings = useCallback(() => {
    firedGroupWarnings.current = new Set();
  }, []);

  return { validateFiles, validateGroups, resetGroupWarnings };
};
